// NotesModal — edit a single booking's notes without leaving the diary.
//
// Server constraint: PATCH /api/v1/collections/bookings/:enc_id only accepts
// paid|collected|highlight_type. Notes have to go through the PUT
// endpoint, which is partial (every field optional). So this modal
// PUTs `{ notes: <new value> }`.
//
// On success: closes, calls onSaved with the new notes, and the page
// invalidates the diary week query so the row re-renders with the new
// note.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Textarea } from '../../../components/form/Textarea';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { useToast } from '../../../hooks/useToast';
import { cn } from '../../../utils/cn';
import type { BookingSummary } from '../../../types/api';
import styles from './NotesModal.module.css';

const MAX_NOTE_LENGTH = 5000;
const WARN_AT = 4500;

export interface NotesModalProps {
  open: boolean;
  booking: BookingSummary | null;
  onClose: () => void;
  /** Called after a successful save. Receives the new notes value. */
  onSaved: (encId: string, notes: string) => void;
}

export function NotesModal({ open, booking, onClose, onSaved }: NotesModalProps): JSX.Element | null {
  const toast = useToast();
  const [value, setValue] = useState(booking?.notes ?? '');
  const [saving, setSaving] = useState(false);

  // Sync the textarea whenever the modal is opened on a different booking.
  useEffect(() => {
    if (open) setValue(booking?.notes ?? '');
  }, [open, booking?.enc_id, booking?.notes]);

  if (!booking) return null;

  const length = value.length;
  const overLimit = length > MAX_NOTE_LENGTH;
  const counterClass = cn(
    styles.counter,
    !overLimit && length >= WARN_AT && styles.counterWarning,
    overLimit && styles.counterError,
  );

  async function handleSave() {
    if (overLimit || !booking) return;
    setSaving(true);
    try {
      await apiClient.put(endpoints.bookings.detail(booking.enc_id), { notes: value });
      toast.success('Notes updated');
      onSaved(booking.enc_id, value);
      onClose();
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ?? 'Could not save notes.'
        : 'Could not save notes.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  const subtitle = `${booking.name} · ${booking.effective_address.address}, ${booking.effective_address.postcode}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Notes"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={overLimit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.subtitle}>{subtitle}</div>
        <Textarea
          autoResize
          rows={6}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Add notes for this booking…"
          aria-label="Notes"
        />
        <span className={counterClass}>
          {length} / {MAX_NOTE_LENGTH}
        </span>
      </div>
    </Modal>
  );
}
